import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodInputSourceStatus,
  runFoodInputIngestion,
  runFoodInputTrendCalculation,
} from "../../../../api/foodApi";

const FoodInputIngestionPage = ({ onClose }) => {
  const [status, setStatus] = useState(null);
  const [inputCategory, setInputCategory] = useState("");
  const [department, setDepartment] = useState("Antioquia");
  const [city, setCity] = useState("Medellín");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(250);
  const [dryRun, setDryRun] = useState(true);
  const [useMock, setUseMock] = useState(true);
  const [running, setRunning] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [trendResult, setTrendResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchFoodInputSourceStatus());
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load input source status.");
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleIngest = async () => {
    try {
      setRunning(true);
      setIngestResult(
        await runFoodInputIngestion({
          sourceKey: "DANE_SIPSA_INPUTS",
          inputCategory: inputCategory || undefined,
          department,
          city,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          limit: Number(limit) || 250,
          dryRun,
          useMock,
        })
      );
      if (!dryRun) await loadStatus();
    } catch (error) {
      setErrorMessage(error.message || "Input ingestion failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleTrends = async () => {
    try {
      setRunning(true);
      setTrendResult(
        await runFoodInputTrendCalculation({
          inputCategory: inputCategory || undefined,
          department,
          city,
          daysBack: 60,
          dryRun,
        })
      );
    } catch (error) {
      setErrorMessage(error.message || "Trend calculation failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-input-ingestion-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>SIPSA Input Price Ingestion</h1>
          <p className="muted">Farm & production inputs — manual runs only.</p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card">
        <h2>Configuration</h2>
        {status ? (
          <ul>
            <li>Health: {status.source?.healthStatus}</li>
            <li>Endpoint: {status.configuration?.endpointConfigured ? "Yes" : "No"}</li>
            <li>Mock: {status.configuration?.useMock ? "Yes" : "No"}</li>
          </ul>
        ) : null}
        <p className="muted">
          Set <code>SIPSA_INPUTS_ENDPOINT_URL</code> or <code>SIPSA_INPUTS_MOCK=true</code>.
        </p>
      </section>

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <label>
            Category
            <select value={inputCategory} onChange={(e) => setInputCategory(e.target.value)}>
              <option value="">All</option>
              <option value="FERTILIZER">FERTILIZER</option>
              <option value="ANIMAL_FEED">ANIMAL_FEED</option>
              <option value="FUEL">FUEL</option>
              <option value="SEED">SEED</option>
              <option value="PACKAGING">PACKAGING</option>
            </select>
          </label>
          <label>
            Department{" "}
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </label>
          <label>
            City <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Date from <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            Date to <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label>
            Limit <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={useMock} onChange={(e) => setUseMock(e.target.checked)} />
            Use mock data
          </label>
          <button type="button" className="btn btn--primary" disabled={running} onClick={handleIngest}>
            Run input ingestion
          </button>
          <button type="button" className="btn btn--ghost" disabled={running} onClick={handleTrends}>
            Calculate trends
          </button>
        </div>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}
      {ingestResult ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(ingestResult, null, 2)}</pre>
      ) : null}
      {trendResult ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(trendResult, null, 2)}</pre>
      ) : null}
    </div>
  );
};

export default FoodInputIngestionPage;
